/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useState } from "react";
import { DateRange, DayPicker } from "react-day-picker";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Button } from "src/components";
import { iavActions } from "src/context/iav-slice";
import { RootState } from "src/context/store";
import FilterAccordion from "src/features/fields/container/FilterAccordion";
import { toast } from "src/hooks/useToast";
import { IAV_CATEGORY, IAV_DATE_TYPES, IAV_STATUS_OPTIONS } from "src/pages/invoice-ack/iav-helpers";

interface Props {
  onClose: () => void;
}

const Divider = () => <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />;

const SORT_OPTIONS = [
  { key: "ASC", value: "Oldest first" },
  { key: "DESC", value: "Newest first" },
];

// Same panel layout as the Invoice Entry / Approval filters (InvoiceFilters.tsx)
const IavQueueFiltersComponent = forwardRef<HTMLElement, Props>(({ onClose }, ref) => {
  const dispatch = useDispatch();
  const { filters, filterOptions } = useSelector((state: RootState) => state.iav);

  const [localDateType, setLocalDateType] = useState<any[]>(filters.dateType || []);
  const [tempFilters, setTempFilters] = useState({
    dateRange: {
      from: filters.dateRange?.from ? new Date(filters.dateRange.from) : undefined,
      to: filters.dateRange?.to ? new Date(filters.dateRange.to) : undefined,
    } as DateRange | undefined,
    fromTime: "00:00",
    toTime: "23:59",
  });

  // Disputes have no assignee — only the stamp-sign flow round-robins one
  const showAssignee = filters.category !== IAV_CATEGORY.DISPUTE;
  const dateTypes = IAV_DATE_TYPES.filter((type) =>
    filters.category === IAV_CATEGORY.DISPUTE ? type.key !== "assigned_at" : type.key !== "dispute_started",
  );

  const clearDateFilter = () => {
    dispatch(iavActions.setFilters({ dateType: [], dateRange: null }));
    setTempFilters({ dateRange: { from: undefined, to: undefined }, fromTime: "00:00", toTime: "23:59" });
    setLocalDateType([]);
  };

  const applyDateFilter = () => {
    if (!localDateType.length) {
      toast({ description: "Please choose a date type first", variant: "destructive" });
      return;
    }
    const { dateRange, fromTime, toTime } = tempFilters;
    if (!dateRange?.from) return;

    const from = new Date(dateRange.from);
    const [fromHours, fromMinutes] = fromTime.split(":");
    from.setHours(Number(fromHours), Number(fromMinutes), 0, 0);

    let to: Date | undefined;
    if (dateRange.to) {
      to = new Date(dateRange.to);
      const [toHours, toMinutes] = toTime.split(":");
      to.setHours(Number(toHours), Number(toMinutes), 0, 0);
    }

    dispatch(
      iavActions.setFilters({
        dateType: localDateType,
        dateRange: { from: from.toISOString(), ...(to && { to: to.toISOString() }) },
      }),
    );
  };

  const resetFilters = () => {
    dispatch(iavActions.resetFilters());
    setTempFilters({ dateRange: { from: undefined, to: undefined }, fromTime: "00:00", toTime: "23:59" });
    setLocalDateType([]);
  };

  return (
    <aside
      ref={ref}
      className="bg-white border-l-[1px] min-w-[24rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0 h-screen"
    >
      <div className="flex items-center justify-between gap-5 mt-3">
        <h3 className="whitespace-nowrap">Fetch filters</h3>
        <Button className="border py-1 bg-[#7F56D9] text-white" onClick={resetFilters}>
          Reset
        </Button>
        <Button className="border py-2 bg-[#7F56D9] text-white" onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="flex flex-col pt-4">
        <FilterAccordion
          uniqueKey="key"
          displayKey="value"
          title="Sort By"
          arrayToFilter={SORT_OPTIONS}
          onSelectingFilter={(value) => dispatch(iavActions.setFilters({ sorting: value }))}
          defaultValue={filters.sorting}
          isSingleSelect={true}
        />
        {showAssignee && (
          <>
            <Divider />
            <FilterAccordion
              uniqueKey="invoice_assignment_id"
              displayKey="user_name"
              title="Assignee"
              arrayToFilter={filterOptions.assignee}
              onSelectingFilter={(value) => dispatch(iavActions.setFilters({ assignee: value }))}
              defaultValue={filters.assignee}
            />
          </>
        )}
        <Divider />
        <FilterAccordion
          uniqueKey="code"
          displayKey="shortname"
          title="Company Name"
          arrayToFilter={filterOptions.company}
          onSelectingFilter={(value) => dispatch(iavActions.setFilters({ company: value, division: [] }))}
          defaultValue={filters.company}
        />
        <Divider />
        <FilterAccordion
          uniqueKey="company_divisioncode"
          displayKey="division_name"
          title="Division"
          arrayToFilter={filterOptions.division}
          onSelectingFilter={(value) => dispatch(iavActions.setFilters({ division: value }))}
          defaultValue={filters.division}
        />
        <Divider />
        <FilterAccordion
          uniqueKey="key"
          displayKey="value"
          title="Status"
          arrayToFilter={IAV_STATUS_OPTIONS[filters.category] ?? []}
          onSelectingFilter={(value) => dispatch(iavActions.setFilters({ status: value }))}
          defaultValue={filters.status}
        />
        <Divider />
        <FilterAccordion displayKey="" title="Date & Time" arrayToFilter={[]} uniqueKey="" onSelectingFilter={() => {}} defaultValue={[]} />
        <div className="my-0">
          <div className="flex items-center gap-2 mb-2">
            <Autocomplete
              placeholder="Date Type"
              suggestionList={dateTypes}
              displayKey="value"
              uniqueKey="key"
              onSelectionChange={(value: any) => setLocalDateType([value])}
              defaultValue={filters.dateType?.[0]}
            />
            <Button className="bg-[#7F56D9] text-white w-[10rem]" onClick={clearDateFilter}>
              Clear
            </Button>
          </div>
          <div className="flex flex-col gap-3">
            <DayPicker
              classNames={{
                today: `border border-[#8051FF]`,
                selected: `bg-[#ded9ff] border-[#8051FF] font-semibold`,
                root: `border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]`,
                day: `p-2 cursor-pointer`,
                chevron: `fill-[#7F56D9] hover:fill-[#8051FF] transition-colors`,
                outside: `text-gray-400`,
              }}
              mode="range"
              defaultMonth={tempFilters.dateRange?.from}
              selected={tempFilters.dateRange}
              onSelect={(range: DateRange | undefined) => setTempFilters({ ...tempFilters, dateRange: range })}
              numberOfMonths={1}
            />
            {tempFilters.dateRange?.from && (
              <div className="flex items-center gap-4 text-sm">
                <div className="flex flex-col">
                  <label className="text-gray-600 mb-1">From Time</label>
                  <input
                    type="time"
                    value={tempFilters.fromTime}
                    onChange={(e) => setTempFilters({ ...tempFilters, fromTime: e.target.value })}
                    className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-gray-600 mb-1">To Time</label>
                  <input
                    type="time"
                    value={tempFilters.toTime}
                    onChange={(e) => setTempFilters({ ...tempFilters, toTime: e.target.value })}
                    className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#8051FF]"
                  />
                </div>
                <Button className="bg-[#7F56D9] text-white w-[10rem] mt-5" onClick={applyDateFilter}>
                  Apply
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
});

export default IavQueueFiltersComponent;
